import type { Route } from 'vue-router';
import type { OAuthProvider, OAuthType } from './types';
import { OAUTH_URL } from './constants';
import { buildQuery, getUtmParams } from '~/components/utils/browser/url';

const allowedExternalDomains = ['bravado.xperiencify.io'];

/**
 * Referer must be relative path or landing on bravado.co,
 * otherwise user could be redirected to phishing site just by ?referer in url
 */
export const getValidReferrer = (url: string | undefined | null) => {
  if (!url) return;
  if (url.startsWith('/')) return `${process.env.internalBaseUrl}${url}`;

  const hostname = new URL(url).hostname;

  if (allowedExternalDomains.includes(hostname)) return url;

  const isValid = url.startsWith(process.env.internalBaseUrl) || hostname.includes('.bravado.co');
  if (isValid) return `${process.env.internalBaseUrl}${url}`;
};

export const buildOAuthUrl = (
  provider: OAuthProvider,
  type: OAuthType,
  route: Route,
  extraParams: Record<string, unknown> = {},
): string => {
  const baseUrl = OAUTH_URL[provider][type];
  const { dynamic_modal, ...rest } = route.query;
  const redirectPath = encodeURIComponent(`${process.env.internalBaseUrl}${route.path}`);

  const query = {
    fail_refferer: process.client && redirectPath,
    refferer: getValidReferrer((rest.refferer || rest.referer) as string) || redirectPath,
    ...rest,
    ...extraParams,
  };

  /**
   * If user landed to signup from external domain we should send an event.
   * But we can't do that after oauth redirect to success page on external domain.
   * So we rewrite refferer to /redirect which log the event and redirect to
   * previous refferer
   */
  const isLoginExternal = route.path === '/login-external';
  if (isLoginExternal) {
    const event = route.query.event;
    const params = {
      ...(event && { event }),
      ...getUtmParams(route.query),
      // We have a mistake in name `refferer` on backed
      referer: query.refferer,
    };
    query.refferer = encodeURIComponent(`${process.env.internalBaseUrl}/redirect${buildQuery(params)}`);
  }

  return `${baseUrl}${buildQuery(query)}`;
};
